import cn from 'classnames'
import {forwardRef, useState} from 'react'
import {sanitizeHtml} from 'src/common/helpers/sanitizeHtml'
import {COUNTRY_NAMES} from '../constants'
import {exportResultsPdf} from '../exportPdf'
import type {CalcResult, Country, CurrencyInfo, Destination} from '../types'
import {fmt} from '../utils'
import {CardHeader} from './CardHeader'

interface ResultsDisplayProps {
  result: CalcResult
  country: Country
  destMode: Destination
  intlDest: string
  exchangeRate: number | null
  exchangeRateLoading: boolean
  destCurrencyInfo: CurrencyInfo | null
  onCopyLink?: () => void
  onExport: () => void
}

export const ResultsDisplay = forwardRef<HTMLDivElement, ResultsDisplayProps>(
  (
    {result, country, destMode, intlDest, exchangeRate, exchangeRateLoading, destCurrencyInfo, onCopyLink, onExport},
    ref,
  ) => {
    const [copied, setCopied] = useState(false)

    const handleCopy = async () => {
      onCopyLink?.()
      setCopied(true)
      setTimeout(() => setCopied(false), 1000)
    }

    if (result.items.length === 0) {
      return (
        <div className="rounded-lg border border-grey-150 bg-moss-white p-8 text-center">
          <p className="text-14 text-grey-450">No allowance applies for the selected combination.</p>
        </div>
      )
    }

    return (
      <div ref={ref} id="per-diem-results" className="animate-fadeInUp20 scroll-mt-18 space-y-4">
        {/* Results */}
        <div className="overflow-hidden rounded-lg border border-grey-150 bg-moss-white">
          <CardHeader step={3} label="Your results" />
          <div className="px-6 pb-4 pt-8">
            <div className="rounded-lg bg-[#002918] p-6">
              <p className="mb-5 text-[11px] font-semibold uppercase tracking-[0.08em] text-moss-white/50">
                Total tax-free allowance
              </p>
              <div className="mb-4 flex flex-wrap items-baseline gap-3">
                <p className="text-[56px] font-normal leading-none tracking-[-0.03em] text-moss-white">
                  {result.currency} {result.total.toFixed(2)}
                </p>
                <CurrencyConversion
                  result={result}
                  country={country}
                  destMode={destMode}
                  intlDest={intlDest}
                  exchangeRate={exchangeRate}
                  exchangeRateLoading={exchangeRateLoading}
                  destCurrencyInfo={destCurrencyInfo}
                />
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center rounded-[4px] border border-moss-white/10 bg-[#0a3d2a] px-4 py-1.5 text-12 font-semibold text-moss-white">
                  {COUNTRY_NAMES[country]}
                  {destMode === 'international' && intlDest
                    ? ` → ${intlDest.includes(' - ') ? intlDest.replace(' - ', ' (') + ')' : intlDest}`
                    : ''}
                </span>
                {result.meta &&
                  result.meta
                    .split('·')
                    .map((p) => p.trim())
                    .filter((p) => p !== intlDest && p !== 'Domestic')
                    .map((part, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center rounded-[4px] border border-moss-white/10 bg-[#0a3d2a] px-4 py-1.5 text-12 font-semibold text-moss-white"
                      >
                        {part}
                      </span>
                    ))}
              </div>
            </div>
          </div>

          <div className="px-6 pb-8 pt-4">
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-grey-450">Breakdown</p>
            <div className="overflow-hidden rounded-lg border border-grey-150">
              {result.items.map((item, index) => (
                <div
                  key={index}
                  className={cn(
                    'grid grid-cols-[1fr_72px] items-center border-b border-grey-135 px-6 py-[9px] text-13 last:border-b-0',
                    item.isTotal && 'bg-grey-120',
                  )}
                >
                  <div>
                    <span className={cn('text-grey-550', item.isTotal && 'font-semibold text-moss-black')}>
                      {item.label}
                    </span>
                    {item.description && <span className="ml-2 text-[11px] text-grey-300">{item.description}</span>}
                  </div>
                  <span
                    className={cn(
                      'text-right font-semibold tabular-nums text-moss-black',
                      item.isTotal && 'font-bold',
                      item.type === 'neg' && 'text-ui-red-450',
                      item.type === 'zero' && 'text-grey-300',
                    )}
                  >
                    {item.value === 0 ? '—' : `${item.type === 'neg' ? '−' : ''}${fmt(item.value, result.currency)}`}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Rules */}
          {result.ruleInfo && (
            <>
              <div className="mx-6 border-b border-grey-150" />
              <div className="px-6 py-8">
                <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-grey-450">
                  Rules applied
                </p>
                <div
                  className="rounded-lg border border-[#c5deff] bg-[#f0f7ff] px-4 py-3 text-13 leading-relaxed text-[#2a4080]"
                  dangerouslySetInnerHTML={{__html: sanitizeHtml(result.ruleInfo)}}
                />
              </div>
            </>
          )}

          {/* Daily log */}
          {result.log && result.log.length > 0 && result.log.some((d) => d.amount !== 0) && (
            <>
              <div className="mx-6 border-b border-grey-150" />
              <div className="px-6 py-8">
                <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-grey-450">
                  Daily breakdown
                </p>
                <div className="overflow-hidden rounded-lg border border-grey-150">
                  <div className="grid grid-cols-[80px_1fr_48px_72px] border-b border-grey-150 bg-grey-120 px-6 py-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-grey-450">
                    <span>Date</span>
                    <span>Type</span>
                    <span className="text-right">Hours</span>
                    <span className="text-right">Amount</span>
                  </div>
                  {result.log.map((day, index) => (
                    <div
                      key={index}
                      className="grid grid-cols-[80px_1fr_48px_72px] items-center border-b border-grey-135 px-6 py-[9px] text-13 last:border-b-0"
                    >
                      <span className="font-medium text-grey-450">{day.date}</span>
                      <span className="text-grey-550">{day.type}</span>
                      <span className="text-right text-grey-300">{day.hours}h</span>
                      <span className="group/tip relative cursor-help text-right font-semibold tabular-nums text-moss-black">
                        {result.currency}
                        {day.amount.toFixed(2)}
                        {day.explanation && (
                          <span className="pointer-events-none invisible absolute bottom-[calc(100%+8px)] right-0 z-10 w-max max-w-[260px] rounded-lg bg-moss-green-900 px-3 py-2 text-12 font-normal leading-normal text-moss-white opacity-0 shadow-elevation-2 transition-all group-hover/tip:visible group-hover/tip:opacity-100">
                            {day.explanation}
                            <span className="absolute right-3 top-full border-[6px] border-transparent border-t-moss-green-900" />
                          </span>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          <div className="flex gap-3 px-6 pb-8 pt-2">
            <button
              onClick={handleCopy}
              aria-label={copied ? 'Link copied to clipboard' : 'Copy shareable link to clipboard'}
              className={cn(
                'text-button-s group relative flex h-12 flex-1 items-center justify-center gap-2 rounded-[4px] px-4 py-3 uppercase transition-all',
                copied ? 'bg-green-dark text-moss-white' : 'bg-moss-white text-black hover:bg-moss-grey-100',
              )}
            >
              <span className="z-10">{copied ? 'Copied!' : 'Copy link'}</span>
              <div className="pointer-events-none absolute inset-0 rounded-[4px] border border-black/[10%]" />
              <span aria-live="assertive" className="sr-only">
                {copied ? 'Link copied to clipboard' : ''}
              </span>
            </button>
            <button
              onClick={() => {
                onExport()
                exportResultsPdf(result, country, destMode, intlDest)
              }}
              aria-label="Export per diem results as PDF"
              className="text-button-s group relative flex h-12 flex-1 items-center justify-center gap-2 rounded-[4px] bg-green-neon px-4 py-3 uppercase text-black transition-all"
            >
              <span className="z-10">Export result</span>
              <div className="pointer-events-none absolute inset-0 rounded-[4px] border border-black/[10%] group-hover:bg-moss-white/[20%]" />
            </button>
          </div>
        </div>
      </div>
    )
  },
)

// ── Internal sub-component ───────────────────────────────────────

const HOME_CURRENCY_INFO: Record<string, {code: string; symbol: string}> = {
  uk: {code: 'GBP', symbol: '£'},
  de: {code: 'EUR', symbol: '€'},
  at: {code: 'EUR', symbol: '€'},
  nl: {code: 'EUR', symbol: '€'},
}

const CurrencyConversion = ({
  result,
  country,
  destMode,
  intlDest,
  exchangeRate,
  exchangeRateLoading,
  destCurrencyInfo,
}: {
  result: CalcResult
  country: Country
  destMode: Destination
  intlDest: string
  exchangeRate: number | null
  exchangeRateLoading: boolean
  destCurrencyInfo: CurrencyInfo | null
}) => {
  if (!destCurrencyInfo || destMode !== 'international' || !intlDest || result.total <= 0) {
    return null
  }

  const home = HOME_CURRENCY_INFO[country]
  const resultIsInDestCurrency = result.currency === destCurrencyInfo.symbol
  const resultIsInHomeCurrency = result.currency === home.symbol

  if (resultIsInHomeCurrency && !resultIsInDestCurrency) {
    if (exchangeRateLoading) {
      return <div className="h-[27px] w-[140px] animate-pulse rounded bg-[rgba(255,255,255,0.1)]" />
    }

    if (!exchangeRate) {
      return null
    }

    const converted = result.total * exchangeRate
    const decimals = destCurrencyInfo.code === 'JPY' || destCurrencyInfo.code === 'HUF' ? 0 : 2
    return (
      <p className="text-[18px] font-normal tracking-[-0.01em] text-[rgba(255,255,255,0.55)]">
        ≈ {destCurrencyInfo.symbol}
        {converted.toFixed(decimals)} <span className="text-[11px] opacity-60">{destCurrencyInfo.code}</span>
      </p>
    )
  }

  if (resultIsInDestCurrency && !resultIsInHomeCurrency) {
    if (exchangeRateLoading) {
      return <div className="h-[27px] w-[140px] animate-pulse rounded bg-[rgba(255,255,255,0.1)]" />
    }

    if (!exchangeRate || exchangeRate === 0) {
      return null
    }

    const converted = result.total / exchangeRate
    return (
      <p className="text-[18px] font-normal tracking-[-0.01em] text-[rgba(255,255,255,0.55)]">
        ≈ {home.symbol}
        {converted.toFixed(2)} <span className="text-[11px] opacity-60">{home.code}</span>
      </p>
    )
  }

  return null
}
